import { isInstant, itemDef, RECIPES, stackOf, STATION_ITEMS, STATION_NAMES, STATION_RADIUS, type ItemKind, type Recipe, type Station } from "@eldrun/content";
import { isDiscovered, maxCraftable, type StackLike } from "@eldrun/sim";
import { KIND_NAMES } from "./inventory-model.ts";

export interface IngredientView {
  item: string;
  name: string;
  have: number;
  need: number;
}

export interface RecipeView {
  id: string;
  output: string;
  name: string;
  qty: number;
  inputs: IngredientView[];
  /** Quantas vezes dá para fabricar agora (0 a 10), por ingredientes e espaço para a saída. */
  craftable: number;
}

/** Receita ainda não descoberta da aba, com os itens que faltam ver. */
export interface LockedHint {
  id: string;
  missing: string[];
}

export interface StationTab {
  station: Station;
  name: string;
  recipes: RecipeView[];
  locked: LockedHint[];
}

/** Abas com fabricação instantânea: Mãos sempre; Bancada e Bigorna quando estão por perto. */
export const CRAFT_TABS: readonly Station[] = ["maos", "bancada", "bigorna"];
/** Quantas receitas bloqueadas cada aba mostra como "próximas descobertas" (doc 03). */
export const LOCKED_HINTS = 2;

const name = (itemId: string) => itemDef(itemId)?.name ?? itemId;
const stationItem = (station: Station) => (station === "maos" ? undefined : STATION_ITEMS[station]);

function count(slots: readonly StackLike[], itemId: string): number {
  let n = 0;
  for (const s of slots) if (s.itemId === itemId) n += s.qty;
  return n;
}

function view(recipe: Recipe, slots: readonly StackLike[]): RecipeView {
  return {
    id: recipe.id,
    output: recipe.output,
    name: name(recipe.output),
    qty: recipe.qty,
    inputs: recipe.inputs.map((i) => ({ item: i.item, name: name(i.item), have: count(slots, i.item), need: i.qty })),
    craftable: maxCraftable(slots, recipe, stackOf),
  };
}

/** O que falta ver para descobrir a receita (o ingrediente-chave, ou os ingredientes desconhecidos). */
function missingKnowledge(recipe: Recipe, known: (itemId: string) => boolean): string[] {
  if (recipe.key) return known(recipe.key) ? [] : [name(recipe.key)];
  return recipe.inputs.filter((i) => !known(i.item)).map((i) => name(i.item));
}

/**
 * Projeção pura das receitas para o painel: por aba, as descobertas (com o que se tem e o que
 * precisa) e as próximas a descobrir. O servidor revalida tudo ao fabricar.
 */
export function craftingTabs(slots: readonly StackLike[], known: (itemId: string) => boolean, near: ReadonlySet<Station>): StationTab[] {
  const tabs: StationTab[] = [];
  for (const station of CRAFT_TABS) {
    if (station !== "maos" && !near.has(station)) continue;
    const recipes: RecipeView[] = [];
    const locked: LockedHint[] = [];
    for (const recipe of RECIPES) {
      if (recipe.station !== station || !isInstant(recipe)) continue;
      if (isDiscovered(recipe, known, stationItem(station))) recipes.push(view(recipe, slots));
      else if (locked.length < LOCKED_HINTS) locked.push({ id: recipe.id, missing: missingKnowledge(recipe, known) });
    }
    tabs.push({ station, name: STATION_NAMES[station], recipes, locked });
  }
  return tabs;
}

/** Estações (de fabricação instantânea) a até STATION_RADIUS do ponto. */
export function stationsNear(structures: Iterable<{ itemId: string; x: number; z: number }>, x: number, z: number): Set<Station> {
  const near = new Set<Station>();
  for (const s of structures) {
    const dx = s.x - x;
    const dz = s.z - z;
    if (dx * dx + dz * dz > STATION_RADIUS * STATION_RADIUS) continue;
    for (const station of CRAFT_TABS) if (station !== "maos" && STATION_ITEMS[station] === s.itemId) near.add(station);
  }
  return near;
}

/**
 * Categorias da fabricação (F62, mockup do dono: abas laterais com ícone), pelo tipo do item que sai.
 * "Todos" mostra tudo; as estações (Mãos, Bancada, Bigorna) continuam como abas no topo.
 */
export const CRAFT_CATEGORIES = [
  { id: "todos", name: "Todos", kinds: [] },
  { id: "ferramentas", name: "Ferramentas", kinds: ["ferramenta"] },
  { id: "armas", name: "Armas", kinds: ["arma", "municao"] },
  { id: "armaduras", name: "Armaduras", kinds: ["armadura"] },
  { id: "consumiveis", name: "Consumíveis", kinds: ["comida"] },
  { id: "materiais", name: "Materiais", kinds: ["material"] },
  { id: "construcao", name: "Construção", kinds: ["construcao", "estacao", "utilitario"] },
] as const satisfies readonly { id: string; name: string; kinds: readonly ItemKind[] }[];

export type CraftCategory = (typeof CRAFT_CATEGORIES)[number]["id"];

/** A receita entra na categoria? ("Todos" aceita tudo; item desconhecido só em "Todos".) */
export function inCategory(category: CraftCategory, output: string): boolean {
  if (category === "todos") return true;
  const kind = itemDef(output)?.kind;
  const def = CRAFT_CATEGORIES.find((c) => c.id === category);
  return kind !== undefined && (def?.kinds as readonly string[] | undefined)?.includes(kind) === true;
}

/** Células da vista de ingredientes (F82, ui-f): 2 × 2 → resultado. */
export const GRID_CELLS = 4;

/**
 * A página `page` dos ingredientes na grade 2 × 2: sempre `GRID_CELLS` células (as que sobram vazias).
 * Receita com mais de quatro ingredientes ganha páginas (os ◆ embaixo da grade); hoje nenhuma passa de três.
 */
export function ingredientPage<T>(inputs: readonly T[], page: number): { cells: (T | undefined)[]; page: number; pages: number } {
  const pages = Math.max(1, Math.ceil(inputs.length / GRID_CELLS));
  const at = Math.min(pages - 1, Math.max(0, Math.floor(page)));
  return { cells: Array.from({ length: GRID_CELLS }, (_, i) => inputs[at * GRID_CELLS + i]), page: at, pages };
}

/** A receita escolhida continua escolhida se ainda aparece; senão, a primeira da lista (ou nenhuma). */
export function pickRecipe(shown: readonly { id: string }[], selected: string | undefined): string | undefined {
  return shown.some((r) => r.id === selected) ? selected : shown[0]?.id;
}

/** Linha de baixo da receita na lista (F82): o tipo do que sai e se dá para fazer agora. */
export function recipeSubtitle(view: Pick<RecipeView, "output" | "craftable">): string {
  const kind = itemDef(view.output)?.kind;
  return `${kind ? KIND_NAMES[kind] : "Item"} · ${view.craftable > 0 ? `dá para fazer ${view.craftable}` : "faltam ingredientes"}`;
}
